
import { Mail } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

interface NavigationLinksProps {
  onClick?: () => void;
  layoutMode: 'horizontal' | 'vertical';
  textSize: 'xs' | 'sm' | 'base';
  showSettings?: boolean;
}

export const NavigationLinks = ({ onClick, layoutMode, textSize, showSettings = false }: NavigationLinksProps) => {
  const location = useLocation();
  const { userRole } = useAuth();
  
  const handleContactClick = () => {
    window.location.href = 'mailto:info@thepeoapp.com';
    if (onClick) onClick();
  };

  const linkClasses = `
    nav-link 
    ${layoutMode === 'horizontal' ? 'px-3 py-2' : 'block px-3 py-2'} 
    rounded-full
    ${textSize === 'xs' ? 'text-xs' : textSize === 'sm' ? 'text-sm' : 'text-base'} 
    font-medium
    ${layoutMode === 'vertical' ? 'text-primary-foreground' : 'text-primary'}
    hover:bg-white/20
    transition-colors
    duration-200
    shadow-sm
  `;

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0 }
  };

  // Add PEO Dashboard link if the user is a PEO provider
  const isPeoUser = userRole === 'peo';
  const isPeoDashboard = location.pathname === '/peo/dashboard';

  return (
    <motion.div
      className={`flex ${layoutMode === 'horizontal' ? 'flex-row space-x-1' : 'flex-col space-y-1'}`}
      variants={container}
      initial="hidden"
      animate="show"
    >
      {isPeoUser && (
        <motion.div variants={item}>
          <Link 
            to="/peo/dashboard" 
            className={`${linkClasses} ${isPeoDashboard ? 'bg-white/30 text-white' : ''}`}
            onClick={onClick}
          >
            PEO Dashboard
          </Link>
        </motion.div>
      )}
      
      <motion.div variants={item}>
        <Link 
          to="/about" 
          className={`${linkClasses} ${location.pathname === '/about' ? 'bg-white/30 text-white' : ''}`}
          onClick={onClick}
        >
          About
        </Link>
      </motion.div>
      
      <motion.div variants={item}>
        <Link 
          to="/partnerships" 
          className={`${linkClasses} ${location.pathname === '/partnerships' ? 'bg-white/30 text-white' : ''}`}
          onClick={onClick}
        >
          Partnerships
        </Link>
      </motion.div>
      
      <motion.div variants={item}>
        <Link 
          to="/peo-vetting-process" 
          className={`${linkClasses} ${location.pathname === '/peo-vetting-process' ? 'bg-white/30 text-white' : ''}`}
          onClick={onClick}
        >
          PEO Vetting Process
        </Link>
      </motion.div>
      
      <motion.div variants={item}>
        <button
          onClick={handleContactClick}
          className={`${linkClasses} flex items-center gap-1`}
        >
          <Mail className={textSize === 'xs' ? 'h-3 w-3' : 'h-4 w-4'} />
          Contact Us
        </button>
      </motion.div>
    </motion.div>
  );
};
